import { Input } from 'components/atoms';
import { DescriptiveInputWrapper } from 'components/molecules';
import ThemeCard from 'components/molecules/card/theme-card/ThemeCard';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import ViewHint from 'components/molecules/hint/ViewHint';
import ApplicationLanguagePopupForm from 'components/molecules/popup/language-select/ApplicationLanguageSelectPopupForm';
import { useLanguagesActions } from 'hooks/useLanguagesOptions';
import { t } from 'i18next';
import i18n from 'locales/i18n';
import { useStoreState } from 'providers/StoreStateContext';
import { useCallback, useMemo } from 'react';
import { THEME_TYPE } from 'store/theme';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';

const UserSettings = () => {
  const { setThemeType, themeType, theme } = useStoreState();
  const { getLanguagesOptions } = useLanguagesActions();
  const { getSelectOptions } = useEnumToSelectOptions();

  const language = useMemo(() => {
    const lng = i18n.language ?? 'en';
    return lng.split('-')[0];
  }, [i18n.language]);

  const languageOptions = useMemo(() => {
    const options = getLanguagesOptions();

    if (options && options.length > 0) {
      return options;
    }

    return [
      {
        label: t('enum.supportedLanguages.pl'),
        value: 'pl',
      },
      {
        label: t('enum.supportedLanguages.en'),
        value: 'en',
      },
    ];
  }, [getLanguagesOptions]);

  const selectedLanguageOption = useMemo(() => {
    const found = languageOptions?.find((o: any) => o?.value === language);

    return (
      found ?? {
        label: t(`enum.supportedLanguages.${language}`),
        value: language,
      }
    );
  }, [languageOptions, language]);

  const handleLanguageChange = useCallback(async (languageCode: string | null) => {
    const next = languageCode ?? 'en';
    await i18n.changeLanguage(next);
  }, []);
  const currentThemeType = themeType ?? THEME_TYPE.light;
  return (
    <DataWrapper>
      {!!theme?.canChangeThemeMode && (
        <>
          <div />
          <ViewHint hintId="selected-theme" />

          <div
            className="grid w-full"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px',
            }}
          >
            <ThemeCard
              theme={THEME_TYPE.light}
              selected={currentThemeType === THEME_TYPE.light}
              onClick={() => setThemeType(THEME_TYPE.light)}
            />
            <ThemeCard
              theme={THEME_TYPE.dark}
              selected={currentThemeType === THEME_TYPE.dark}
              onClick={() => setThemeType(THEME_TYPE.dark)}
            />
          </div>
        </>
      )}
      <div />
      <ViewHint hintId="application-language" />
      <ApplicationLanguagePopupForm
        initialLanguageCode={language}
        options={languageOptions}
        onSelectLanguage={async (languageCode, actions) => {
          await handleLanguageChange(languageCode);
          actions.close();
        }}
        trigger={
          <div className="flex w-full cursor-pointer">
            <Input.Text
              name=""
              label={t('input.label.language')}
              value={selectedLanguageOption?.label ?? ''}
              readOnly
              style={{ pointerEvents: 'none' }}
            />
          </div>
        }
      />
    </DataWrapper>
  );
};

export default UserSettings;
